import { useEffect, useMemo, useState } from 'react';

import { useCurrentCountryConfiguration } from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { getMetadata } from '@rover/rsdk/src/modules/Logging/internals';
import { LoggingMetadata } from '@rover/rsdk/src/modules/Logging/types';

import { RoverStatsigUser } from '../types';

export function useRoverStatsigUser(): {
  roverStatsigUser: RoverStatsigUser;
  isLoading: boolean;
  error: Error | null;
} {
  const { user, isLoading: isUserLoading } = useCurrentUser();
  const { data: countryConfig, isLoading: isCountryConfigLoading } =
    useCurrentCountryConfiguration();

  const [loggingMetadata, setLoggingMetadata] = useState<Partial<LoggingMetadata>>();
  const [isMetadataLoading, setIsMetadataLoading] = useState<boolean>(true);
  const [metadataError, setMetadataError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMetadata = async (): Promise<void> => {
      try {
        const metadata = await getMetadata();
        setLoggingMetadata(metadata);
      } catch (error) {
        setMetadataError(error as Error);
        captureError(error as Error);
      } finally {
        setIsMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const roverStatsigUser: RoverStatsigUser = useMemo(
    () => ({
      appVersionName: loggingMetadata?.appVersionName,
      country: countryConfig?.countryCode,
      email: user?.email,
      source: loggingMetadata?.platform?.toLowerCase(),
      userID: user?.opk,
      vendorID: loggingMetadata?.uniqueID,
    }),
    [loggingMetadata, countryConfig, user]
  );

  const isLoading = isUserLoading || isCountryConfigLoading || isMetadataLoading;
  const error: Error | null = metadataError;

  return { roverStatsigUser, isLoading, error };
}
